import React, { useEffect, useReducer, useState } from 'react'
import {
    Box,
    Select,
    InputLabel,
    MenuItem,
    FormControl,
    IconButton,
    Divider
} from '@mui/material'

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { DemoContainer } from "@mui/x-date-pickers/internals/demo"

import VisibilityIcon from "@mui/icons-material/Visibility"
import './styles.css'
import defaultReducer from '../../../utils/defaultReducer'
import api from '../../../api'
import Loading from '../../Loading'
import { getSession } from '../../../auth'
import AllCharts from './graphs/charts'
import dayjs from 'dayjs';

import DashboardChartVisibilityDialog from "../../Dialogs/DashboardChartVisibility"
import { translations } from "../../Dialogs/DashboardChartVisibility"

let timeout = 0

const default_interval = 45000

const possible_times = {
    '8' : 'Últimas 8 horas',
    '12': 'Últimas 12 horas',
    '24': 'Últimas 24 horas'
}

const retrieveData = async (queue, setState, enterprise_id, loader = false) => {
    if (Number(dayjs().format('mm')) % 5 !== 0 && !loader) return
    if (loader) setState({ loading: true })
    const res = await api.get(`api/enterprises/${enterprise_id}/dashboard/historical/${queue ? `?queue=${queue}` : ''}`)
    if (res.status === 200) {
        setState({ data: res.data, loading: false })
        return
    }
    setState({ loading: false })
}

const retrievePeriod = async (periodStart, periodEnd, setState, enterprise_id, loader = false) => {
    if (loader) setState({ loading: true })
    const res = await api.get(`api/enterprises/${enterprise_id}/dashboard/historical/period/`, {
        params: {
            start: dayjs(periodStart).format("YYYY-MM-DD"),
            end: dayjs(periodEnd).format("YYYY-MM-DD")
        }
    })
    if (res.status === 200) {
        setState({ data: res.data, loading: false })
        return
    }
    setState({ loading: false })
}

const HistoricalDash = ({ innerRef, queues }) => {

    const enterprise = getSession()?.profile?.enterprise
    const enterprise_id = enterprise?.id
    const timezone = enterprise.settings.timezone

    const [ visibleCharts, setVisibleCharts ] = useState(() => {
        const stored = localStorage.getItem("historic-visible-charts")
        return stored ? JSON.parse(stored) : Object.keys(translations).reduce((acc, key) => {
            acc[key] = true
            return acc
        }, {})
    })

    useEffect(() => {
        localStorage.setItem("historic-visible-charts", JSON.stringify(visibleCharts))
    }, [visibleCharts])

    const [ showVisibilityDialog, setShowVisibilityDialog ] = useState(false)

    const [state, setState] = useReducer(defaultReducer, {
        data: {},
        selected_queue: '__all__',
        loading: false,
        interval_filter: '8',
        periodStart: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
        periodEnd: new Date()
    })

    const date = dayjs()
    const division_rest = date.get('minute') % 5
    const last_date = date.subtract(division_rest, 'minutes').tz(timezone).format('HH:mm')

    useEffect(() => {
        innerRef(
            <Box sx={{display:'flex', gap:'1rem'}}>
                <FormControl fullWidth size="small" sx={{ maxWidth: '15rem' }}>
                    <InputLabel id="hour-select-label">Intervalo</InputLabel>
                    <Select
                        labelId="hour-select-label"
                        id="hour-box-simple-select"
                        onChange={(event) => setState({ interval_filter: event.target.value })}
                        value={state.interval_filter}
                        label='Intervalo'
                    >
                        {Object.entries(possible_times)?.map(([key, value]) => (
                            <MenuItem key={`${key}-select-hour`} value={key}>{value}</MenuItem>
                        ))}
                        <Divider component="li" sx={{ my: 0.5 }} />
                        <MenuItem key="period" value="period">Período</MenuItem>
                    </Select>
                </FormControl>
                {state.interval_filter !== "period" &&
                    <FormControl fullWidth size="small" sx={{ maxWidth: '15rem' }}>
                        <InputLabel id="queue-select-label">Fila(s)</InputLabel>
                        <Select
                            labelId="queue-select-label"
                            id="queue-box-simple-select"
                            onChange={(event) => setState({ selected_queue: event.target.value })}
                            value={state.selected_queue}
                            label='Fila(s)'
                        >
                            <MenuItem key={`0-select-queue`} value={'__all__'}>Todas</MenuItem>
                            {queues?.map(each => (
                                <MenuItem key={`${each.id}-select-queue`} value={each.name}>{each.description}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                }
                {state.interval_filter === "period" &&
                    <>
                        <Box sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DemoContainer components={["DatePicker", "DatePicker"]} sx={{ width: "100%", marginTop: "-8px" }}>
                                    <DatePicker
                                        label="Início"
                                        value={(state.periodStart) ? dayjs(state.periodStart) : null}
                                        onChange={(newValue) => setState({ periodStart: newValue })}
                                        slotProps={{ textField: { size: "small" } }}
                                        sx={{ width: "100%" }}
                                    />
                                    <DatePicker
                                        label="Fim"
                                        value={(state.periodEnd) ? dayjs(state.periodEnd) : null}
                                        onChange={(newValue) => setState({ periodEnd: newValue })}
                                        slotProps={{ textField: { size: "small" } }}
                                        sx={{ width: "100%" }}
                                    />
                                </DemoContainer>
                            </LocalizationProvider>
                        </Box>
                    </>
                }
                <IconButton onClick={() => setShowVisibilityDialog(true)}>
                    <VisibilityIcon />
                </IconButton>
            </Box>
        )
        clearInterval(timeout)
        if (state.interval_filter !== "period") {
            retrieveData(state.selected_queue, setState, enterprise_id, true)
            timeout = setInterval(() => retrieveData(state.selected_queue, setState, enterprise_id), default_interval)
        } else
            retrievePeriod(state.periodStart, state.periodEnd, setState, enterprise_id, true)
        return () => clearInterval(timeout)
    }, [state.selected_queue, state.interval_filter, state.periodStart, state.periodEnd])

    useEffect(() => {
        setState({ selected_queue: "__all__" })
    }, [state.interval_filter])

    return (
        <>
            <Loading loading={state.loading} />
            <Box className='historical-records-mainframe'>
                <AllCharts
                    data={state.data}
                    mode={state.selected_queue}
                    interval={state.interval_filter}
                    last_date={last_date}
                    visibleCharts={visibleCharts}
                />
            </Box>
            {showVisibilityDialog &&
                <DashboardChartVisibilityDialog
                    open={showVisibilityDialog}
                    close={() => setShowVisibilityDialog(false)}
                    visibleCharts={visibleCharts}
                    setVisibleCharts={setVisibleCharts}
                />
            }
        </>
    )
}

export default HistoricalDash
